import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { Box, Flex } from "@rebass/grid/emotion";
import { Hide } from "@rebass/hide";
import { graphql } from "gatsby";
import React from "react";
import { Helmet } from "react-helmet";
import Icon from "react-icons-kit";
import { cameraRetro } from "react-icons-kit/fa/cameraRetro";
import { comments } from "react-icons-kit/fa/comments";
import { folderOpen } from "react-icons-kit/fa/folderOpen";
import { gavel } from "react-icons-kit/fa/gavel";
import { heart } from "react-icons-kit/fa/heart";
import { magic } from "react-icons-kit/fa/magic";
import { phone } from "react-icons-kit/fa/phone";
import { search } from "react-icons-kit/fa/search";
import { trophy } from "react-icons-kit/fa/trophy";
import { users } from "react-icons-kit/fa/users";
import { LinkButton } from "../../components/Button";
import Card from "../../components/Card";
import Contact from "../../components/Contact";
import GoogleReviews from "../../components/GoogleReviews";
import Hero from "../../components/Hero";
import Page from "../../components/Page";
import PageBlock from "../../components/PageBlock";
import PageTitle from "../../components/PageTitle";
import ProfileImage from "../../components/ProfileImage";
import RoundedImage from "../../components/RoundedImage";
import Service from "../../components/Service";
import References from "../../components/References";
import SectionTitle from "../../components/SectionTitle";
import Testimonials from "../../components/Testimonials";
import { Link } from "gatsby";
import ImmoweltBadge from "../../components/ImmoweltBadge";
import SectionTeam from "../../components/SectionTeam";

const WideBackground = styled(Flex)`
  background: radial-gradient(
    90% 150% at 50% 120%,
    rgba(189, 18, 30),
    rgba(105, 31, 131)
  );
  color: white;
  margin-bottom: 100px;
  margin-top: 100px;
`;

const BigIcon = styled(Box)`
  text-align: center;
  color: #961452;
  margin-bottom: 32px;
`;

const ServiceCard = styled(Card)`
  margin-bottom: 32px;
`;

const ImmobilienmaklerSaalfeldRudolstadt = ({ data }) => (
  <Page
    address={{
      streetAddress: "Breitscheidstraße 92",
      postalCode: "07407",
      city: "Rudolstadt",
      phone: "0174 3217733",
    }}
    header={
      <Hero fluid={data.homeHeroImage.fluid}>
        <Flex
          alignItems="center"
          flexWrap="wrap"
          css={css`
            max-width: 50em;
          `}
        >
          <Box
            width={[1, null, 2 / 3]}
            css={css`
              text-align: left;
            `}
          >
            <PageTitle>Immobilienmakler Saalfeld-Rudolstadt</PageTitle>
            <p>
              Sie haben vor Ihre Immobilie in Saalfeld-Rudolstadt zu verkaufen
              oder brauchen einen guten Rat von einem Immobilienmakler? Linda
              Bäumer ist Ihre Immobilien-Expertin speziell für die Region
              Saalfeld-Rudolstadt. Gemeinsam mit Kranich Immobilien können Sie
              die vor Ihnen liegenden Herausforderungen entspannt angehen und
              zum Erfolg führen.
            </p>
            <Flex alignItems="stretch" flexWrap="wrap">
              <Box
                width="80px"
                mr={3}
                mb={3}
                css={css`
                  border-radius: 12px;
                  overflow: hidden;
                `}
              >
                <a
                  href="https://www.immowelt.de/profil/0b968dea9cdf44ea97eaa81b55e82cdb"
                  target="_blank"
                  rel="noreferrer"
                >
                  <Hide xsmall>
                    <ImmoweltBadge />
                  </Hide>
                </a>
              </Box>
              <GoogleReviews />
            </Flex>
          </Box>
          <Hide width={[1, null, 1 / 3]} paddingLeft={5} xsmall small>
            <ProfileImage version={2} />
            <p>
              <strong>Linda Bäumer</strong>
              <br />
              Makler Saalfeld-Rudolstadt
            </p>
          </Hide>
        </Flex>
      </Hero>
    }
  >
    <Helmet
      title="Immobilienmakler in Saalfeld-Rudolstadt"
      meta={[
        {
          name: "description",
          content:
            "Als erfahrener und erfolgreicher Immobilienmakler in Saalfeld-Rudolstadt hilft Ihnen Linda Bäumer beim Kauf und Verkauf von Immobilien.",
        },
      ]}
    />

    <PageBlock>
      <Flex justifyContent="center">
        <Box width={[1, null, "50em"]} my={4} p={3}>
          <Card collapsed>
            <Flex alignItems="center" flexWrap="wrap" mx={-3}>
              <Box width={[1, 1, 1 / 3]} px={[3, 3, 3]} mb={[3, 3, 0]}>
                <RoundedImage
                  fluid={data.founderImage.fluid}
                  alt="Linda Bäumer"
                />
              </Box>
              <Box width={[1, null, 2 / 3]} px={3}>
                <h2>Linda Bäumer — Immobilienmakler Saalfeld-Rudolstadt</h2>
                <p>
                  Linda Bäumer ist seit vielen Jahren erfolgreicher Makler im
                  Landkreis Saalfeld-Rudolstadt. Mit Linda an Ihrer Seite haben
                  Sie einen verlässlichen und professionellen Partner an der
                  Seite, der den Immobilienmarkt in der Region bestens kennt.
                </p>
              </Box>
            </Flex>
          </Card>
        </Box>
      </Flex>

      {/* Not needed for the moment */}

      {/*
      <Flex justifyContent="center">
        <Box width={[1, null, "30em"]} my={2} p={3}>
          <p style={{ textAlign: "center" }}>
            Was mich in Erfurt so erfolgreich macht? Zielorientiertes Arbeiten
            mit Immobilien aller Art (Haus, Wohnung & Grundstück) gepaart mit
            meinen zwischenmenschlichen Fähigkeiten.{" "}
            <strong>Der Mensch steht bei mir stets an erster Stelle.</strong>
          </p>
        </Box>
      </Flex>
      */}

      <Flex justifyContent="center">
        <Box width={[1, null, "50em"]} my={4} p={3}>
          <Flex justifyContent="center" flexWrap="wrap" mx={-3}>
            <Box width={[1, null, 1 / 3]} px={3}>
              <BigIcon>
                <Icon size={50} icon={trophy} />
              </BigIcon>
              <h4>Kompetenz & Erfahrung</h4>
              <p>
                Seit 2012 begleitet Linda Bäumer erfolgreich zahlreiche Kunden.
                Nur wenige Makler können mit ihren Erfolgen mithalten. Dank
                ihrer Erfahrung und übergreifenden Kompetenz führt Sie jedes
                Projekt erfolgreich zu Ende.
              </p>
            </Box>
            <Box width={[1, null, 1 / 3]} px={3}>
              <BigIcon>
                <Icon size={50} icon={comments} />
              </BigIcon>
              <h4>Vertrauen dank Transparenz</h4>
              <p>
                Beim Verkauf von Immobilien ist Vertrauen zwischen Eigentümer
                und Makler entscheidend. Dank einer transparenten Arbeitsweise
                und unaufgeregter Ehrlichkeit fühlt man sich bei Linda stets gut
                aufgehoben.
              </p>
            </Box>
            <Box width={[1, null, 1 / 3]} px={3}>
              <BigIcon>
                <Icon size={50} icon={heart} />
              </BigIcon>
              <h4>Mit Herz</h4>
              <p>
                Hinter jeder Immobilie steht immer auch eine Geschichte und
                Menschen. Mit Einfühlungsvermögen und Herzlichkeit schafft Linda
                während der gemeinsamen Arbeit eine warme Atmosphäre.
              </p>
            </Box>
          </Flex>
        </Box>
      </Flex>

      <Flex justifyContent="center">
        <Box width={[1, null, "50em"]} my={4} p={3}>
          <Card collapsed>
            <Flex alignItems="center" flexWrap="wrap" mx={-3}>
              <Box width={[1, 1, 1 / 3]} px={[3, 3, 3]} mb={[3, 3, 0]}>
                <RoundedImage
                  fluid={data.localImage.fluid}
                  alt="Saalfeld-Rudolstadt"
                />
              </Box>
              <Box width={[1, null, 2 / 3]} px={3}>
                <h2>Die Region Saalfeld-Rudolstadt</h2>
                <p>
                  Im Jahr 1995 wurde durch die Zusammenlegung der Landkreise
                  Saalfeld und Rudolstadt der neue Landkreis Saalfeld-Rudolstadt
                  geschaffen. Saalfeld-Rudolstadt gehört heute zu der schönsten
                  Region des gesamten Freistaates Thüringen. Nicht weniger als
                  16 Naturschutzgebiete zeugen von der Idylle, die sich neben
                  der Saale im Thüringer Schiefergebirge ausbreitet. Jeder, der
                  die Natur liebt, wird sein Herz an diese Region verschenken.
                </p>
                <p>
                  Als Immobilienmakler für Saalfeld-Rudolstadt hilft Ihnen Linda
                  Bäumer und Ihr Team bei allen Fragen rund um Immobilien. Egal
                  ob Sie Hilfe in den großen Städten wie
                  <Link to="/immobilienmakler-saalfeld/"> Saalfeld </Link>
                  und
                  <Link to="/immobilienmakler-rudolstadt/"> Rudolstadt </Link>
                  benötigen oder in den kleineren Städten und Gemeinden wie
                  <Link to="/immobilienmakler-unterwellenborn/">
                    {" "}
                    Unterwellenborn
                  </Link>
                  ,
                  <Link to="/immobilienmakler-bad-blankenburg/">
                    {" "}
                    Bad Blankenburg
                  </Link>
                  ,
                  <Link to="/immobilienmakler-uhlstaedt-kirchhasel/">
                    {" "}
                    Uhlstädt-Kirchhasel
                  </Link>
                  ,<Link to="/immobilienmakler-leutenberg/"> Leutenberg</Link>,
                  <Link to="/immobilienmakler-koenigsee/"> Königsee</Link>,
                  <Link to="/immobilienmakler-kaulsdorf/"> Kaulsdorf </Link>
                  oder
                  <Link to="/immobilienmakler-schwarzatal/"> Schwarzatal</Link>.
                </p>
              </Box>
            </Flex>
          </Card>
        </Box>
      </Flex>

      <Flex justifyContent="center">
        <Box width={[1, null, "50em"]} my={4} p={3}>
          <SectionTitle center>
            Immobilie in Saalfeld-Rudolstadt verkaufen
          </SectionTitle>
          <p style={{ textAlign: "center" }}>
            Wenn Sie Ihre Immobilie erfolgreich verkaufen möchten, sollten Sie
            professionell vorgehen. Schon kleine Fehler können Eigentümer sehr
            viel Geld kosten. Ebenfalls sollten Sie den anfallenden
            Arbeitsaufwand nicht unterschätzen. Gerne können Sie sich
            zurücklehnen und von unserem Rundum-Sorglos-Paket profitieren. Wir
            übernehmen beim Verkauf Ihrer Immobilie sämtliche Aufgaben und das
            Projekt schnellstmöglich zu einem erfolgreichen Ende.
          </p>
          <Flex flexWrap="wrap">
            <Service
              icon={search}
              title="Immobilienbewertung"
              description="Bei der Immobilienbewertung wird nicht geschätzt, sondern der Wert Ihrer Immobilie fach- und sachgerecht ermittelt."
            />
            <Service
              icon={cameraRetro}
              title="Fotografie und Exposés"
              description="Aussagekräftige Werbemittel sind das A und O. Deshalb werden für Sie ausschließlich hochprofessionelle Werbemittel in bester Qualität erstellt."
            />
            <Service
              icon={magic}
              title="Vermarktung"
              description="Ihre Immobilie wird proaktiv vermarktet. Mit einer regionalen und überregionalen Platzierung des Objektes können wir reichweitenstark potenzielle Käufer ansprechen."
            />
            <Service
              icon={folderOpen}
              title="Bürokratie überwinden"
              description="Die Bürokratie hat die Immobilienwirtschaft fest im Griff. Aber keine Sorge: Es werden alle notwendigen Unterlagen für Sie bereitgestellt und aufgearbeitet."
            />
            <Service
              icon={users}
              title="Besichtigung und Käuferprüfung"
              description="Von der Anfrage bis zur Besichtigung und Bonitätsprüfung. Es wird der perfekte Käufer auf Grundlage Ihrer Kriterien gefunden."
            />
            <Service
              icon={gavel}
              title="Verträge und Notar"
              description="Sobald der passende Käufer gefunden wurde, werden die letzten Schritte bis zur notariellen Beglaubigung sauber vorbereitet."
            />
          </Flex>
        </Box>
      </Flex>

      <Flex justifyContent="center">
        <Box width={[1, null, "50em"]} p={3}>
          <ServiceCard red collapsed>
            <h3>Exklusives Homestaging</h3>
            <p>
              Wussten Sie: Weniger als 20 % aller Menschen können sich leere
              Räume möbliert vorstellen und deren Dimensionen richtig
              einschätzen. Als Immobilienmakler weiß ich, dass es für den ersten
              Eindruck keine zweite Chance gibt. Mit dem Homestaging-Service
              verhelfe ich Ihrer Immobilie zum perfekten ersten Eindruck.
            </p>
            <div>
              <LinkButton to="/homestaging/">Mehr zum Homestaging</LinkButton>
            </div>
          </ServiceCard>
        </Box>
      </Flex>
    </PageBlock>

    <WideBackground justifyContent="center">
      <Box
        width={[1, null, "50em"]}
        my={4}
        p={3}
        css={css`
          text-align: center;
        `}
      >
        <h2>
          Exklusive & erfolgreiche Vermarktung von Immobilien in
          Saalfeld-Rudolstadt
        </h2>
        <p>
          Immobilien-Verkäufer vertrauen auf die exklusive Vermarktung von
          Kranich Immobilien.
        </p>
        <Flex alignItems="center" justifyContent="center" flexWrap="wrap">
          <LinkButton light href="/kontakt">
            Jetzt Nachricht an Linda Bäumer senden
          </LinkButton>
          <a
            href="tel: 036450 44 99 33"
            css={css`
              color: white;
              padding: 0 24px;
            `}
          >
            <Icon size={32} icon={phone} />
            <span> 036450 / 44 99 33</span>
          </a>
        </Flex>
      </Box>
    </WideBackground>

    <PageBlock>
      <Flex justifyContent="center">
        <Box width={[1, null, "50em"]} p={3}>
          <SectionTitle center>
            Immobilien in Saalfeld-Rudolstadt kaufen
          </SectionTitle>
          <p>
            Es gibt viele wunderschöne Immobilien in Saalfeld-Rudolstadt, die
            nur auf Sie warten. Als Makler für Saalfeld-Rudolstadt helfe ich
            Ihnen bei der Suche nach Ihrem Traumobjekt.
          </p>
          <Box mx={[0, 0, -40]}>
            <ServiceCard red collapsed>
              <h3>Aktuelle Angebote</h3>
              <p>
                Sie suchen ein Objekt zum Kauf in Saalfeld-Rudolstadt? Schauen
                Sie in die Übersicht unserer aktuellen Angebote. Wir haben
                spannende Immobilien aus Saalfeld-Rudolstadt und darüber hinaus.
              </p>
              <div>
                <LinkButton to="/angebote/">Zu den Angeboten</LinkButton>
              </div>
            </ServiceCard>
            <ServiceCard blue collapsed>
              <h3>Finanzierung</h3>
              <p>
                Die Finanzierung ist ein wesentlicher Eckpunkt bei der Erfüllung
                Ihres Traums. Dank Partnerschaften mit Finanzexperten können wir
                Sie umfangreich beraten und bei der Finanzierung unterstützen.
              </p>
              <div>
                <LinkButton to="/finanzierung/">Zur Finanzierung</LinkButton>
              </div>
            </ServiceCard>
            {/*
            <ServiceCard purple collapsed>
              <h3>VIP-Suche</h3>
              <p>
                Die Suche nach einem passenden Haus oder der perfekten
                Eigentumswohnung kann in Erfurt zum Marathon werden. Nutzen Sie
                meinen VIP-Service, damit ich Ihnen bei Ihrer Suche
                vollumfänglich helfen kann.
              </p>
              <div>
                <LinkButton to="/angebote/">
                  Zur VIP Suche und aktuellen Angeboten
                </LinkButton>
              </div>
            </ServiceCard>

            <ServiceCard red collapsed>
              <h3>Exklusives Homestaging</h3>
              <p>
                Weniger als 20% aller Menschen können sich leere Räume möbliert
                vorstellen und deren Dimensionen richtig abschätzen. Als
                Immobilienmakler weiß ich: Für den ersten Eindruck gibt es keine
                zweite Chance. Mit dem Homestaging-Service verhelfe ich Ihrer
                Immobilie zum perfekten ersten Eindruck.
              </p>
              <div>
                <LinkButton to="/homestaging/">Mehr zum Homestaging</LinkButton>
              </div>
            </ServiceCard>
            */}
          </Box>
        </Box>
      </Flex>
    </PageBlock>

    <PageBlock>
      <SectionTeam />
    </PageBlock>

    <PageBlock>
      <Flex justifyContent="center">
        <Box width={[1, null, "70em"]} my={5} p={3} justifyContent="center">
          <SectionTitle center>Was meine Kunden über mich sagen</SectionTitle>
          <Testimonials data={data.testimonials} />
          <Flex
            justifyContent="center"
            flexDirection="column"
            alignItems="center"
          >
            <p>Viele weitere Stimmen zu meiner Arbeit finden Sie auf Google:</p>
            <Box width={[1, "20em"]}>
              <GoogleReviews />
            </Box>
          </Flex>
        </Box>
      </Flex>

      <Flex justifyContent="center">
        <Box width={[1, null, "60em"]} my={5} p={3} justifyContent="center">
          <SectionTitle center>Referenzobjekte</SectionTitle>
          <Box width={[1, null, "40em"]} margin="auto">
            <p style={{ textAlign: "center" }}>
              Erzählen kann man vieles. Doch ich habe viele Erfolge
              nachzuweisen. Ganz gleich ob Einfamilienhaus, Eigentumswohnung
              oder ungewöhnliche Objekte — aber machen Sie sich doch ein eigenes
              Bild:
            </p>
          </Box>
          <References data={data.references} />
        </Box>
      </Flex>

      <Flex justifyContent="center">
        <Box width={[1, null, 1 / 2]} my={5} p={3}>
          <Card red>
            <ProfileImage />
            <Box mt={3}>
              <h3>Herausforderungen gemeinsam angehen</h3>
              <p>
                Möchten Sie mit mir über Ihre Immobilie sprechen? Füllen Sie
                dafür bitte das Formular aus. Ich melde mich danach so schnell
                wie möglich bei Ihnen. Einfach, schnell und unverbindlich.
              </p>
              <Contact />
            </Box>
          </Card>
        </Box>
      </Flex>
    </PageBlock>
  </Page>
);

export const query = graphql`
  query {
    homeHeroImage: imageSharp(
      fluid: { originalName: { regex: "/home-hero/" } }
    ) {
      fluid(maxWidth: 1600, quality: 80) {
        ...GatsbyImageSharpFluid_withWebp
      }
    }
    founderImage: imageSharp(fluid: { originalName: { regex: "/linda_2/" } }) {
      fluid(maxWidth: 500, maxHeight: 500, quality: 80) {
        ...GatsbyImageSharpFluid_withWebp
      }
    }
    localImage: imageSharp(
      fluid: { originalName: { regex: "/placeholder-local/" } }
    ) {
      fluid(maxWidth: 500, quality: 80) {
        ...GatsbyImageSharpFluid_withWebp
      }
    }
    references: allMarkdownRemark(
      filter: { fileAbsolutePath: { regex: "/src/references/" } }
      sort: { fields: [frontmatter___order] }
      limit: 3
    ) {
      edges {
        node {
          frontmatter {
            image {
              childImageSharp {
                fluid(maxWidth: 500, quality: 80) {
                  ...GatsbyImageSharpFluid_withWebp
                }
              }
            }
            location
            type
            order
            difficulty
            price
            time
          }
          html
        }
      }
    }
    testimonials: allMarkdownRemark(
      filter: { fileAbsolutePath: { regex: "/src/testimonials/" } }
      sort: { fields: [fileAbsolutePath] }
    ) {
      edges {
        node {
          frontmatter {
            image {
              childImageSharp {
                fluid(maxWidth: 250, quality: 80) {
                  ...GatsbyImageSharpFluid_withWebp
                }
              }
            }
            author
          }
          html
        }
      }
    }
  }
`;

export default ImmobilienmaklerSaalfeldRudolstadt;
